import { useQuery } from '@tanstack/react-query';

import { ConsumptionPreset, ConsumptionPresetsRequestMessage } from '../../../types/ConsumptionPresetsRequestMessage';
import { VesselTankMessage } from '../../../types/VesselTankMessage';
import { useToasts } from '../../cdl/Toast/useToasts';
import { queries } from '../../common/api/queryKeys/queries';
import { translate } from '../../common/helpers/translate.helper';
import { LoadingIndicator } from '../../common/LoadingIndicator/LoadingIndicator';
import { createOverlay } from '../../common/Overlay/Overlay';
import { Box } from '../../common/ui/Box';

import { EditPresetsForm } from './form/EditPresetsForm';
import { useConsumptionPresets } from './hooks/useConsumptionPresets';
import { useVesselConsumptionPresetsMutation } from './hooks/useVesselConsumptionPresetsMutation';

interface EditPresetsProps {
    vesselId: string;
    customerId: string;
    onClose: () => void;
}

export const EditPresetsOverlay = (props: EditPresetsProps) => {
    return createOverlay(<EditPresets {...props} />);
};

const EditPresets = ({ onClose, vesselId, customerId }: EditPresetsProps) => {
    const presetsQuery = useConsumptionPresets({ vesselId });
    const tanksQuery = useQuery({
        ...queries.vessels.detail(vesselId)._ctx.tanks,
        select: (response: { vesselTanks: VesselTankMessage[] }) => [
            ...new Set(response.vesselTanks.map((tank) => tank.category)),
        ],
    });
    const presetsMutation = useVesselConsumptionPresetsMutation(vesselId);
    const { addToast, addErrorToast } = useToasts();

    const onSubmit = async (updatedPresets: ConsumptionPreset[]) => {
        const presets: ConsumptionPresetsRequestMessage = {
            vesselId: vesselId,
            customerId: customerId,
            presets: updatedPresets,
        };
        await presetsMutation.mutateAsync(
            { vesselId, presets },
            {
                onSuccess: () => {
                    addToast(translate('consumption.editPresets.presetsUpdateSuccessToast'));
                    onClose();
                },
                onError: () => {
                    addErrorToast(translate('consumption.editPresets.presetsUpdateErrorToast'));
                },
            }
        );
    };

    if (!presetsQuery.data || (tanksQuery.isFetching && !tanksQuery.isFetched)) {
        return <LoadingIndicator variant="full-height" />;
    }

    return (
        <Box paddingBottom={64} minHeight="100%" maxWidth="800px" marginX="auto">
            <EditPresetsForm
                tankCategories={tanksQuery.data ?? []}
                presets={presetsQuery.data.presets}
                onClose={onClose}
                onSubmit={onSubmit}
            />
        </Box>
    );
};
